/**
 * El registro de marcas: qué marcas existen y cómo se activa cada una.
 *
 * Salió de `traxion.ts` (SL-1921), donde vivía por historia. Cada marca define
 * su tema en su propio archivo; acá solo se registra.
 */

import type { ThemeConfig } from 'antd';
import { cssVariables } from '../emitVars';
import { antdTheme } from '../config';
import { TRAXION_ANTD, TRAXION_CSS } from './traxion';
import { PRISMHR_ANTD, PRISMHR_CSS } from './prismhr';
import { BRAND_META, type BrandMeta } from './meta';
import { prismhrAssets, siliaAssets, traxionAssets, type BrandAssetSet } from './assets';

/* ═══════════════════════════════════════════════════════════════════════
   8. TEMPLATES — runtime multi-brand
   ═══════════════════════════════════════════════════════════════════════ */

/**
 * A template is a cohesive set of tokens that re-skins the whole app without
 * touching component logic. Two mechanisms work together:
 *
 *   1. A class on `<body>` (`bodyClass`) overrides the `--silia-*` variables,
 *      which is what hand-written CSS reads.
 *   2. `themeFor(id)` returns the AntD `ConfigProvider` config, so AntD
 *      primitives repaint too.
 *
 * ── Cómo se elige ─────────────────────────────────────────────────────────
 * Cada deploy elige su marca con `REACT_APP_THEME` (ADR-044): lo lee
 * `envDefaultResolver` para el runtime y `vite.config.ts` para `index.html`.
 * Un valor que no es un `TemplateId` corre `default`.
 */
export type TemplateId = 'default' | 'traxion' | 'prismhr';

export type TemplateDef = {
  id: TemplateId;
  label: string;
  description: string;
  /** Body class that activates the matching `--silia-*` overrides. */
  bodyClass: string;
  /** Passed to AntD's ConfigProvider so its primitives repaint too. */
  themeConfig: ThemeConfig;
  /** CSS emitted under `body.<bodyClass>`; empty for the default template. */
  cssOverrides: string;
  /** The brand's logos and background images, by role (SL-1920). */
  assets: BrandAssetSet;
};

export const TEMPLATES: Record<TemplateId, TemplateDef> = {
  default: {
    id: 'default',
    label: 'Silia',
    description: 'Marca por defecto — lime + purple, DM Sans, radius 12px',
    bodyClass: '',
    themeConfig: antdTheme,
    cssOverrides: '',
    assets: siliaAssets,
  },
  traxion: {
    id: 'traxion',
    label: 'Traxión',
    description: 'Plataforma logística — lime + grays neutrales, Inter, radius 6px',
    bodyClass: 'template-traxion',
    themeConfig: TRAXION_ANTD,
    cssOverrides: TRAXION_CSS,
    assets: traxionAssets,
  },
  prismhr: {
    id: 'prismhr',
    label: 'PrismHR',
    description: 'Azul CTA + navy, rampa en su tono con la luminancia de Silia',
    bodyClass: 'template-prismhr',
    themeConfig: PRISMHR_ANTD,
    cssOverrides: PRISMHR_CSS,
    assets: prismhrAssets,
  },
};

export const TEMPLATE_LIST: TemplateDef[] = [
  TEMPLATES.default,
  TEMPLATES.traxion,
  TEMPLATES.prismhr,
];

export const DEFAULT_TEMPLATE: TemplateId = 'default';

/** AntD config for a template, falling back to the default. */
export function themeFor(id: TemplateId): ThemeConfig {
  return TEMPLATES[id]?.themeConfig ?? TEMPLATES.default.themeConfig;
}

/** Body class for a template — apply it to `document.body`. */
export function bodyClassFor(id: TemplateId): string {
  return TEMPLATES[id]?.bodyClass ?? '';
}

/**
 * Every template's CSS overrides, concatenated. Inject alongside
 * `cssVariables`; the rules only apply once the matching body class is set, so
 * shipping them all costs nothing until a template is activated.
 */
export const templateCssOverrides: string = TEMPLATE_LIST.map((t) => t.cssOverrides)
  .filter(Boolean)
  .join('\n');

/** `cssVariables` plus every template override — the whole stylesheet. */
export const cssVariablesWithTemplates: string = `${cssVariables}\n${templateCssOverrides}`;

// Cada marca tiene su título, favicon y manifest para `index.html`: si se agrega
// un `TemplateId` sin su entrada en `meta.ts`, esta línea no compila. No exporta
// nada: en runtime es una lectura sin efecto; el valor está en el chequeo de tipos.
BRAND_META satisfies Record<TemplateId, BrandMeta>;
